import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import { receberVersaoWeb } from '../sistema/atualizacao';
import { somarSkillMagia } from '../world/skillsMagia';
import { playConsumivel, playEvento } from '../audio/mixer';
import { anotarQueda } from '../suporte/coleta';
import { avisarSuporte } from '../suporte/aviso';
import { avisarCorreio } from '../correio/aviso';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import { ehRealceFaixa, type RealceFaixa } from '../avisos/regras';
import { argsDoAviso, defDoAviso } from '../i18n/avisos';
import { existe, numero, t } from '../i18n/idioma';
import { receberMundo } from '../data/mundoSync';
import { anotarPong, overlayAberto } from './desempenho';
import { shardHeaderValue } from '../api/http';
import { ehEfeitoAlvo, folhaDaMagia } from '../world/efeitos';
import { avisoSobreMim, flutuarSobreMim } from '../world/engine';
import { nomeDaMagia, nomeDoItem, nomeDoMapa } from '../i18n/nomes';
import type { ColetaAoVivo } from '../api/types';
import { MS_POR_MINUTO, sincronizarHora } from '../world/relogio';
import type { BuffAtivo } from '../world/buffs';
import type {
  ChatCatchup,
  ChatLine,
  TellCatchup,
  DueloConvite,
  DueloEnviado,
  DueloEstado,
  DueloRodada,
  DueloVida,
  DueloVoltar,
  TradeInvite,
  TradeOver,
  TradeState,
  TradeSlot,
  TradeSide,
  NetEntered,
  NetFrame,
  NetMoved,
  NetReject,
  NetSaid,
  NetSelf,
  NetStrike,
  NetWelcome,
  WorldHandle,
  CorpseContents,
  TreinoAlvo
} from '../world/types';

function pick(raw: unknown, index: number, ...names: string[]): unknown {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) return raw[index];
  const rec = raw as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(rec, index)) return rec[index];
  if (Object.prototype.hasOwnProperty.call(rec, String(index))) return rec[String(index)];
  for (const name of names) {
    if (name in rec) return rec[name];
    const camel = name.charAt(0).toLowerCase() + name.slice(1);
    if (camel in rec) return rec[camel];
  }
  return undefined;
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'bigint') return Number(v);
  return typeof v === 'number' ? v : Number(v) || fallback;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function optionalStr(v: unknown): string | undefined {
  return v === undefined || v === null ? undefined : str(v);
}

function flag(v: unknown): boolean {
  return v === true || v === 1;
}

function optionalFlag(v: unknown): boolean | undefined {
  if (v === undefined || v === null) return undefined;
  return flag(v);
}

function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** Um ganho que o perfil aplica na hora: skill (id e quanto subiu) ou atributo (str/dex/int e o valor novo). */
export interface GanhoDoPerfil {
  readonly tipo: 'skill' | 'atributo' | 'xp';
  readonly id: string;
  readonly valor: number;
  /** Só no tipo 'xp' (aviso 255): as barras que mudaram numa morte, com o custo do próximo +0,1. */
  readonly barras?: readonly { readonly id: string; readonly xp: number; readonly custo: number }[];
}

/** Aviso 255 (techspec-xp-skills §05.2): trincas [skillId, barra, custo]. */
function barrasDoAviso(args: readonly string[]): { id: string; xp: number; custo: number }[] {
  const out: { id: string; xp: number; custo: number }[] = [];
  for (let i = 0; i + 2 < args.length; i += 3) {
    const xp = Number(args[i + 1]), custo = Number(args[i + 2]);
    if (args[i] && Number.isFinite(xp) && Number.isFinite(custo)) out.push({ id: args[i], xp, custo });
  }
  return out;
}

/** Evento `co` (ColetaEstadoWire): o gump da coleta. `ativa` falso fecha. */
function decodeColeta(raw: unknown): ColetaAoVivo {
  const torasRaw = pick(raw, 7, 'Toras', 'toras');
  const toras: Record<string, number> = {};
  if (torasRaw instanceof Map) for (const [k, v] of torasRaw) toras[String(k)] = num(v);
  else if (torasRaw && typeof torasRaw === 'object')
    for (const [k, v] of Object.entries(torasRaw as Record<string, unknown>)) toras[k] = num(v);
  return {
    ativa: flag(pick(raw, 0, 'Ativa', 'ativa')),
    fase: num(pick(raw, 1, 'Fase', 'fase')),
    arvore: num(pick(raw, 2, 'Arvore', 'arvore')),
    skill: num(pick(raw, 3, 'Skill', 'skill')),
    skillInicio: num(pick(raw, 4, 'SkillInicio', 'skillInicio')),
    tentativas: num(pick(raw, 5, 'Tentativas', 'tentativas')),
    acertos: num(pick(raw, 6, 'Acertos', 'acertos')),
    toras,
    terminaEm: num(pick(raw, 8, 'TerminaEm', 'terminaEm')),
    bonus: num(pick(raw, 9, 'Bonus', 'bonus')),
    ferramenta: str(pick(raw, 17, 'Ferramenta', 'ferramenta')),
    parado: str(pick(raw, 10, 'Parado', 'parado')),
    proximaEm: num(pick(raw, 11, 'ProximaEm', 'proximaEm')),
    tentativaMs: num(pick(raw, 12, 'TentativaMs', 'tentativaMs')),
    ultimo: num(pick(raw, 13, 'Ultimo', 'ultimo')),
    ultimaTora: str(pick(raw, 14, 'UltimaTora', 'ultimaTora')),
    ultimaQtd: num(pick(raw, 15, 'UltimaQtd', 'ultimaQtd')),
    origem: str(pick(raw, 16, 'Origem', 'origem')),
    recurso: str(pick(raw, 18, 'Recurso', 'recurso')) || 'madeira',
    recebidoEm: Date.now()
  };
}

function decodeCorpseContents(raw: unknown): CorpseContents {
  const slots = arr(pick(raw, 2, 'Slots', 'slots')).map(row => ({
    defId: str(pick(row, 0, 'DefId', 'defId')),
    qty: num(pick(row, 1, 'Qty', 'qty')),
    // A chave 3 era o hue: o material agora vem no próprio defId (`katana@valorite`).
    id: guid(pick(row, 2, 'Id', 'id'))
  }));
  return {
    id: num(pick(raw, 0, 'Id', 'id')),
    untilMs: num(pick(raw, 1, 'UntilMs', 'untilMs')),
    slots,
    ownerName: str(pick(raw, 3, 'OwnerName', 'ownerName')),
    mobDefId: str(pick(raw, 4, 'MobDefId', 'mobDefId'))
  };
}

function decodeWelcome(raw: unknown): NetWelcome {
  return {
    youId: num(pick(raw, 0, 'YouId', 'youId')),
    tick: num(pick(raw, 1, 'Tick', 'tick')),
    roomKey: str(pick(raw, 2, 'RoomKey', 'roomKey')),
    x0: num(pick(raw, 3, 'X0', 'x0')),
    y0: num(pick(raw, 4, 'Y0', 'y0')),
    x1: num(pick(raw, 5, 'X1', 'x1')),
    y1: num(pick(raw, 6, 'Y1', 'y1')),
    spawnX: num(pick(raw, 7, 'SpawnX', 'spawnX')),
    spawnY: num(pick(raw, 8, 'SpawnY', 'spawnY')),
    cityId: str(pick(raw, 9, 'CityId', 'cityId')),
    cityLabel: str(pick(raw, 10, 'CityLabel', 'cityLabel')),
    canal: num(pick(raw, 11, 'Canal', 'canal')),
    protegida: pick(raw, 20, 'Protegida', 'protegida') === true,
    mundoEtag: str(pick(raw, 12, 'MundoEtag', 'mundoEtag')),
    esquemaVersao: num(pick(raw, 13, 'EsquemaVersao', 'esquemaVersao')),
    mundoNome: str(pick(raw, 14, 'MundoNome', 'mundoNome')),
    oculto: !!pick(raw, 15, 'Oculto', 'oculto'),
    peAndarMs: num(pick(raw, 16, 'PeAndarMs', 'peAndarMs')),
    peCorrerMs: num(pick(raw, 17, 'PeCorrerMs', 'peCorrerMs')),
    montadoAndarMs: num(pick(raw, 18, 'MontadoAndarMs', 'montadoAndarMs')),
    montadoCorrerMs: num(pick(raw, 19, 'MontadoCorrerMs', 'montadoCorrerMs'))
  };
}

function decodePong(raw: unknown): { mundoEtag: string; esquemaVersao: number; mundoNome: string } {
  const espera = num(pick(raw, 5, 'EsperaMs', 'esperaMs'));
  const saude = num(pick(raw, 6, 'Saude', 'saude'));
  const sala = num(pick(raw, 7, 'SalaP99', 'salaP99'));
  anotarPong(espera, saude, sala);
  return {
    mundoEtag: str(pick(raw, 2, 'MundoEtag', 'mundoEtag')),
    esquemaVersao: num(pick(raw, 3, 'EsquemaVersao', 'esquemaVersao')),
    mundoNome: str(pick(raw, 4, 'MundoNome', 'mundoNome'))
  };
}

function decodeSelf(raw: unknown): NetSelf {
  return {
    ackSeq: num(pick(raw, 0, 'AckSeq', 'ackSeq')),
    x: num(pick(raw, 1, 'X', 'x')),
    y: num(pick(raw, 2, 'Y', 'y')),
    stam: num(pick(raw, 3, 'Stam', 'stam')),
    targetId: num(pick(raw, 4, 'TargetId', 'targetId')),
    act: num(pick(raw, 5, 'Act', 'act')),
    dir: num(pick(raw, 6, 'Dir', 'dir')),
    hits: num(pick(raw, 7, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 8, 'HitsMax', 'hitsMax')),
    mana: num(pick(raw, 9, 'Mana', 'mana')),
    manaMax: num(pick(raw, 10, 'ManaMax', 'manaMax')),
    poisoned: optionalFlag(pick(raw, 11, 'Poisoned', 'poisoned')),
    stamMax: num(pick(raw, 12, 'StamMax', 'stamMax')),
    warTargetId: num(pick(raw, 13, 'WarTargetId', 'warTargetId')),
    fadiga: num(pick(raw, 14, 'Fadiga', 'fadiga')),
    fadigaEm: num(pick(raw, 15, 'FadigaEm', 'fadigaEm')),
    fadigaFaixa: num(pick(raw, 16, 'FadigaFaixa', 'fadigaFaixa')),
    recoverMs: num(pick(raw, 17, 'RecoverMs', 'recoverMs')),
    criminalLeft: num(pick(raw, 18, 'CriminalLeft', 'criminalLeft')),
    paralizado: flag(pick(raw, 19, 'Paralizado', 'paralizado')),
    z: num(pick(raw, 20, 'Z', 'z')),
    salto: flag(pick(raw, 21, 'Salto', 'salto')),
    pergaminhoXpS: num(pick(raw, 22, 'PergaminhoXpS', 'pergaminhoXpS')),
    pergaminhoXpPct: num(pick(raw, 23, 'PergaminhoXpPct', 'pergaminhoXpPct')),
    pergaminhoXpCorre: flag(pick(raw, 24, 'PergaminhoXpCorre', 'pergaminhoXpCorre'))
  };
}

function decodeMoved(raw: unknown): NetMoved {
  return {
    id: num(pick(raw, 0, 'Id', 'id')),
    x: num(pick(raw, 1, 'X', 'x')),
    y: num(pick(raw, 2, 'Y', 'y')),
    dir: num(pick(raw, 3, 'Dir', 'dir')),
    act: num(pick(raw, 4, 'Act', 'act')),
    hits: num(pick(raw, 5, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 6, 'HitsMax', 'hitsMax')),
    poisoned: optionalFlag(pick(raw, 7, 'Poisoned', 'poisoned')),
    stam: num(pick(raw, 8, 'Stam', 'stam')),
    stamMax: num(pick(raw, 9, 'StamMax', 'stamMax')),
    passos: decodePassos(pick(raw, 10, 'Passos', 'passos')),
    passoMs: num(pick(raw, 11, 'PassoMs', 'passoMs')),
    z: num(pick(raw, 12, 'Z', 'z'))
  };
}

/** byte[] do MessagePack chega como Uint8Array (bin); JSON ou servidor antigo, como array ou ausente. */
function decodePassos(v: unknown): number[] | undefined {
  if (v instanceof Uint8Array) return v.length ? Array.from(v) : undefined;
  if (Array.isArray(v)) return v.length ? v.map(Number) : undefined;
  return undefined;
}

function decodeLook(raw: unknown): {
  skinHue?: number;
  hairStyle?: string;
  hairHue?: number;
  beardStyle?: string;
  beardHue?: number;
  feminino?: boolean;
} {
  const hue = num(pick(raw, 5, 'SkinHue', 'skinHue'));
  const hairHue = num(pick(raw, 7, 'HairHuePub', 'hairHuePub'))
    || num(pick(raw, 2, 'HairHue', 'hairHue'));
  const hairStyle = str(pick(raw, 6, 'HairStyle', 'hairStyle'));
  const beardStyle = str(pick(raw, 8, 'BeardStyle', 'beardStyle'));
  const beardHue = num(pick(raw, 9, 'BeardHue', 'beardHue'));
  const sex = num(pick(raw, 0, 'Sex', 'sex'));
  return {
    ...(hue >= 1002 && hue <= 1058 ? { skinHue: hue } : {}),
    ...(hairStyle ? { hairStyle } : {}),
    ...(hairHue >= 1102 && hairHue <= 1149 ? { hairHue } : {}),
    ...(beardStyle ? { beardStyle } : {}),
    ...(beardHue >= 1102 && beardHue <= 1149 ? { beardHue } : {}),
    ...(sex === 1 ? { feminino: true } : sex === 0 ? { feminino: false } : {})
  };
}

function decodeEntered(raw: unknown): NetEntered {
  const look = decodeLook(pick(raw, 6, 'Look', 'look'));
  const defId = str(pick(raw, 18, 'DefId', 'defId'));
  return {
    ...decodeMoved(raw),
    // No Entered o z é a chave 29 (a 12 é HitsMax).
    z: num(pick(raw, 29, 'Z', 'z')),
    name: str(pick(raw, 4, 'Name', 'name')),
    notoriety: num(pick(raw, 5, 'Notoriety', 'notoriety')),
    mount: str(pick(raw, 7, 'Mount', 'mount')),
    skin: str(pick(raw, 8, 'Skin', 'skin')) || 'ghost',
    skinHue: look.skinHue,
    hairStyle: look.hairStyle,
    hairHue: look.hairHue,
    beardStyle: look.beardStyle,
    beardHue: look.beardHue,
    feminino: look.feminino,
    dead: flag(pick(raw, 9, 'Dead', 'dead')),
    cloth: str(pick(raw, 10, 'Cloth', 'cloth')),
    hits: num(pick(raw, 11, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 12, 'HitsMax', 'hitsMax')),
    mana: num(pick(raw, 13, 'Mana', 'mana')),
    manaMax: num(pick(raw, 14, 'ManaMax', 'manaMax')),
    guildTag: str(pick(raw, 15, 'GuildTag', 'guildTag')),
    rel: num(pick(raw, 16, 'Rel', 'rel')),
    kind: num(pick(raw, 17, 'Kind', 'kind')) === 4
      ? 'guard'
      : num(pick(raw, 17, 'Kind', 'kind')) === 3 || defId.startsWith('dummy-')
      ? 'dummy'
      : num(pick(raw, 17, 'Kind', 'kind')) === 2
      ? 'corpse'
      : num(pick(raw, 17, 'Kind', 'kind')) === 1 || !!defId ? 'monster' : 'player',
    defId,
    aparencia: str(pick(raw, 22, 'Aparencia', 'aparencia')),
    cargo: num(pick(raw, 23, 'Cargo', 'cargo')),
    vip: flag(pick(raw, 24, 'Vip', 'vip')),
    selo: num(pick(raw, 28, 'Selo', 'selo')),
    oculto: flag(pick(raw, 25, 'Oculto', 'oculto')),
    // No Entered as chaves 8 e 9 são Skin e Dead: o vigor mora na 26 e na 27.
    stam: num(pick(raw, 26, 'Stam', 'stam')),
    stamMax: num(pick(raw, 27, 'StamMax', 'stamMax')),
    poisoned: flag(pick(raw, 19, 'Poisoned', 'poisoned')),
    weapon: optionalStr(pick(raw, 20, 'Weapon', 'weapon')),
    shield: optionalStr(pick(raw, 21, 'Shield', 'shield'))
  };
}

function decodeSaid(raw: unknown): NetSaid {
  return {
    id: num(pick(raw, 0, 'Id', 'id')),
    text: str(pick(raw, 1, 'Text', 'text'))
  };
}

function decodeStrike(raw: unknown): NetStrike {
  const flags = num(pick(raw, 4, 'Flags', 'flags'));
  const kindN = num(pick(raw, 5, 'Kind', 'kind'));
  return {
    targetId: num(pick(raw, 0, 'TargetId', 'targetId')),
    dealt: num(pick(raw, 1, 'Dealt', 'dealt')),
    hits: num(pick(raw, 2, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 3, 'HitsMax', 'hitsMax')),
    miss: (flags & 2) !== 0,
    kill: (flags & 1) !== 0,
    crit: (flags & 4) !== 0,
    dodge: (flags & 8) !== 0,
    kind: kindN === 3 ? 'cura' : kindN === 2 ? 'poison' : kindN === 1 ? 'magic' : 'melee',
    atacante: num(pick(raw, 6, 'Atacante', 'atacante'))
  };
}

function decodeCarryDelta(raw: unknown): { defId: string; qty: number } {
  return {
    defId: str(pick(raw, 0, 'DefId', 'defId')),
    qty: num(pick(raw, 1, 'Qty', 'qty'))
  };
}

function decodeFrame(raw: unknown): NetFrame {
  return {
    tick: num(pick(raw, 0, 'Tick', 'tick')),
    self: decodeSelf(pick(raw, 1, 'Self', 'self')),
    moved: arr(pick(raw, 2, 'Moved', 'moved')).map(decodeMoved),
    entered: arr(pick(raw, 3, 'Entered', 'entered')).map(decodeEntered),
    left: arr(pick(raw, 4, 'Left', 'left')).map(v => num(v)),
    said: arr(pick(raw, 5, 'Said', 'said')).map(decodeSaid),
    strikes: arr(pick(raw, 6, 'Strikes', 'strikes')).map(decodeStrike)
  };
}

function decodeReject(raw: unknown): NetReject {
  return {
    seq: num(pick(raw, 0, 'Seq', 'seq')),
    reason: num(pick(raw, 1, 'Reason', 'reason')),
    x: num(pick(raw, 2, 'X', 'x')),
    y: num(pick(raw, 3, 'Y', 'y'))
  };
}

/**
 * A chave 1 do `Notice` é a **ponte** entre os dois servidores: hoje vem a
 * frase pronta (`Message`), depois vem o vetor de argumentos (`Args`). String
 * = servidor antigo; array = servidor novo. O cliente lê os dois e sempre
 * prefere o código, que existe desde sempre.
 */
function decodeNoticeArgs(raw: unknown): string[] {
  const bruto = pick(raw, 2, 'Args', 'args');
  if (Array.isArray(bruto)) return bruto.map(v => str(v));
  return [];
}

/** Só para o servidor que ainda manda frase; some quando o servidor virar. */
function decodeNoticeLegado(raw: unknown): string {
  const bruto = pick(raw, 1, 'Message', 'message');
  return typeof bruto === 'string' ? bruto : '';
}

function decodeNoticeRealce(raw: unknown): RealceFaixa | 0 {
  const n = num(pick(raw, 3, 'Realce', 'realce'));
  return ehRealceFaixa(n) ? n : 0;
}

/**
 * Eventos agendados (techspec-eventos): o servidor manda o id do item e a raridade crua; a frase quer o nome
 * no idioma do jogador e a raridade traduzida.
 */
/** Tempo de buff na língua do jogador: "2 min", "1 min 30 s", "45 s". */
function tempoDoBuff(segundos: number): string {
  const s = Math.max(0, Math.round(segundos));
  const m = Math.floor(s / 60), r = s % 60;
  if (m && r) return t('buff.tempo.minSeg', { m: numero(m), s: numero(r) });
  if (m) return t('buff.tempo.min', { m: numero(m) });
  return t('buff.tempo.seg', { s: numero(r) });
}

function argsDoEvento(code: number, args: readonly string[]): readonly string[] {
  // techspec-pocoes: o tipo do buff vem em id.
  if (code === 210 || code === 212) return [t(`buff.tipo.${args[0] ?? ''}`)];
  if (code === 211) return [t(`buff.tipo.${args[0] ?? ''}`), numero(Number(args[1] ?? 0))];
  if (code === 213) {
    const valor = Number(args[1] ?? 0);
    return [t(`buff.tipo.${args[0] ?? ''}`), valor > 0 ? ` +${numero(valor)}` : '', tempoDoBuff(Number(args[2] ?? 0))];
  }
  // techspec-magias-de-efeito: a magia vem em id.
  if (code >= 217 && code <= 221) return [nomeDaMagia(args[0] ?? ''), args[1] ?? ''];
  if (code === 222 || code === 223 || code === 245) return [nomeDaMagia(args[0] ?? '')];
  if (code === 181) return [nomeDoItem(args[0] ?? ''), args[1] ?? '', t(`evento.raridade.${args[2] ?? 'comum'}`)];
  if (code === 182) return [args[0] ?? '', nomeDoItem(args[1] ?? ''), t(`evento.raridade.${args[2] ?? 'epico'}`)];
  if (code === 177 || code === 178) return [args[0] ?? '', numero(Number(args[1] ?? 0)), args[2] ?? ''];
  if (code === 180) return [args[0] ?? '', numero(Number(args[1] ?? 0))];
  // Chefe do Mundo: o mapa vem em id; o item, em id; a prata, em número.
  if (code === 183) return [args[0] ?? '', args[1] ?? '', nomeDoMapa(args[2] ?? '')];
  if (code === 184) return [args[0] ?? '', nomeDoMapa(args[1] ?? '')];
  if (code === 187) return [numero(Number(args[0] ?? 0)), args[1] ?? '', args[2] ?? ''];
  if (code === 188) return [args[0] ?? '', nomeDoItem(args[1] ?? ''), t(`evento.raridade.${args[2] ?? 'epico'}`)];
  // Pentagrama: a cor vem em id (fogo, gelo, terra, ar, vazio); o motivo da recusa, em id.
  const cor = (i: number): string => args[i] ?? 'fogo';
  if (code === 193) return [args[0] ?? '', t(`penta.ponta.${cor(1)}`)];
  if (code === 194 || code === 197) return [t(`penta.ponta.${cor(0)}`)];
  if (code === 195 || code === 196) return [t(`penta.guardiao.${cor(0)}`)];
  if (code === 198) return [args[0] ?? '', t(`penta.fragmento.${cor(1)}`)];
  if (code === 199) return [args[0] ?? '', t(`penta.orbe.${cor(1)}`)];
  if (code === 201) return [t(`penta.orbe.${cor(0)}`)];
  if (code === 202) return [t(`penta.motivo.${args[0] ?? 'falhou'}`)];
  if (code === 206) {
    const motivo = args[0] ?? '';
    const chave = `cacar.voltaMotivo.${motivo}`;
    return [existe(chave) ? t(chave, { nome: args[1] ?? '' }) : t('cacar.voltaMotivo.outro')];
  }
  if (code === 203) return [t(`penta.orbe.${cor(0)}`), args[1] ?? ''];
  return args;
}

/** Traduz pelo código; sem código conhecido, mostra o que o servidor mandou. */
function textoDoAviso(code: number, args: readonly string[], legado: string): string {
  const def = defDoAviso(code, args);
  if (def) return t(def.chave, argsDoAviso(def, argsDoEvento(code, args)));
  return legado || t('mundo.avisoDoServidor');
}

function decodeChatLine(raw: unknown): ChatLine | null {
  const via = num(pick(raw, 0, 'Via', 'via'));
  if (via !== 1 && via !== 2 && via !== 3 && via !== 4 && via !== 5) return null;
  const name = str(pick(raw, 1, 'Name', 'name'));
  const text = str(pick(raw, 2, 'Text', 'text'));
  if (!name || !text) return null;
  return {
    via: via as 1 | 2 | 3 | 4 | 5,
    name,
    text,
    at: num(pick(raw, 3, 'At', 'at'), Date.now()),
    with: str(pick(raw, 4, 'With', 'with')) || undefined,
    vip: flag(pick(raw, 5, 'Vip', 'vip')),
    selo: num(pick(raw, 6, 'Selo', 'selo'))
  };
}

function decodeTellCatchup(raw: unknown): TellCatchup {
  return {
    with: str(pick(raw, 0, 'With', 'with')),
    lines: arr(pick(raw, 1, 'Lines', 'lines')).map(decodeChatLine).filter((x): x is ChatLine => x !== null)
  };
}

const TELL_NOTICE = new Set([8, 9, 10]);
/** Chat recusado (2) e globo mudo (7): servem a todo canal; logo depois de um tell, são do tell. */
const TELL_RECUSA = new Set([2, 7]);
const CAST_REFUSE = new Set([18, 19, 20, 21, 22, 23, 24, 25, 32, 42, 43, 56, 215]);
/** Uso abortado: some o overlay otimista. 79 (ainda em espera) não entra — o CD continua. */
/** Avisos de prêmio que também põem carta no correio (ranking, sorteio, chefe, pentagrama): o correio relê o resumo. */
const CORREIO_RELE = new Set([160, 181, 187, 198]);
const CONSUME_ABORT = new Set([37, 78, 80, 81, 82, 112, 141, 208, 209, 210, 211]);
/** Dano/erro/morte vão no frame do swing. Aviso cedo faz a caveira "já estar morta". */
const COMBATE_NO_FRAME = new Set([45, 46, 47]);

function guid(v: unknown): string {
  if (typeof v === 'string') return v.toLowerCase();
  if (typeof v === 'number' || typeof v === 'bigint') return String(v);
  const fromNums = Array.isArray(v) && v.length === 16 && v.every(x => typeof x === 'number')
    ? v as number[]
    : null;
  const bytes = v instanceof Uint8Array
    ? v
    : ArrayBuffer.isView(v)
      ? new Uint8Array(v.buffer, v.byteOffset, v.byteLength)
      : fromNums
        ? Uint8Array.from(fromNums)
        : null;
  if (bytes && bytes.length === 16) {
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return v == null ? '' : String(v);
}

export function sameGuid(a: string, b: string | undefined): boolean {
  if (!b) return false;
  return a.replace(/-/g, '').toLowerCase() === b.replace(/-/g, '').toLowerCase();
}

export function isEmptyGuid(id: string): boolean {
  const n = id.replace(/-/g, '').toLowerCase();
  return n.length === 0 || /^0+$/.test(n);
}

function decodeTradeSlot(raw: unknown): TradeSlot {
  return {
    id: guid(pick(raw, 0, 'Id', 'id')),
    defId: str(pick(raw, 1, 'DefId', 'defId')),
    name: str(pick(raw, 2, 'Name', 'name')),
    qty: num(pick(raw, 3, 'Qty', 'qty')),
    dur: (() => {
      const v = pick(raw, 4, 'Dur', 'dur');
      return v == null ? null : num(v);
    })()
  };
}

function decodeTradeSide(raw: unknown): TradeSide {
  return {
    items: arr(pick(raw, 0, 'Items', 'items')).map(decodeTradeSlot),
    gold: num(pick(raw, 1, 'Gold', 'gold')),
    accepted: Boolean(pick(raw, 2, 'Accepted', 'accepted'))
  };
}

function decodeTradeState(raw: unknown): TradeState {
  return {
    id: guid(pick(raw, 0, 'Id', 'id')),
    peer: str(pick(raw, 1, 'Peer', 'peer')),
    mine: decodeTradeSide(pick(raw, 2, 'Mine', 'mine')),
    theirs: decodeTradeSide(pick(raw, 3, 'Theirs', 'theirs')),
    tipo: str(pick(raw, 4, 'Tipo', 'tipo')) || 'troca',
    modo: str(pick(raw, 5, 'Modo', 'modo'))
  };
}

function decodeTradeInvite(raw: unknown): TradeInvite {
  return { name: str(pick(raw, 0, 'Name', 'name')) };
}

function decodeTradeOver(raw: unknown): TradeOver {
  return {
    code: num(pick(raw, 0, 'Code', 'code')),
    message: str(pick(raw, 1, 'Message', 'message'))
  };
}

export interface PartyMemberView {
  readonly characterId: string;
  readonly name: string;
  readonly leader: boolean;
  /** 0 vivo, 1 morto, 2 offline, 3 noutro mapa. */
  readonly state: number;
  readonly hits: number;
  readonly hitsMax: number;
  readonly city: string;
  /** Nenhum vital chegou ainda: a barra desenha apagada. */
  readonly unknown: boolean;
  /** Placas do grupo (techspec-grupo-placas §03): mana, vigor e o retrato. Servidor antigo: zero e sem retrato. */
  readonly mana: number;
  readonly manaMax: number;
  readonly stam: number;
  readonly stamMax: number;
  readonly retrato: RetratoDoMembro | null;
}

/** A aparência do membro para o retrato da placa (o mesmo boneco do topo). */
export interface RetratoDoMembro {
  readonly skin: string;
  readonly skinHue?: number;
  readonly hairStyle?: string;
  readonly hairHue?: number;
  readonly beardStyle?: string;
  readonly beardHue?: number;
  readonly feminino?: boolean;
  readonly cloth: string;
  readonly weapon: string;
  readonly shield: string;
}

/** Mana, vigor e retrato: as mesmas chaves no estado (8–16) e no vital (5–13). */
function decodeExtrasDoMembro(raw: unknown, base: number): Pick<PartyMemberView, 'mana' | 'manaMax' | 'stam' | 'stamMax' | 'retrato'> {
  const look = pick(raw, base + 4, 'Look', 'look');
  const skin = str(pick(raw, base + 5, 'Skin', 'skin'));
  return {
    mana: num(pick(raw, base, 'Mana', 'mana')),
    manaMax: num(pick(raw, base + 1, 'ManaMax', 'manaMax')),
    stam: num(pick(raw, base + 2, 'Stam', 'stam')),
    stamMax: num(pick(raw, base + 3, 'StamMax', 'stamMax')),
    retrato: look == null || !skin ? null : {
      ...decodeLook(look),
      skin,
      cloth: str(pick(raw, base + 6, 'Cloth', 'cloth')),
      weapon: str(pick(raw, base + 7, 'Weapon', 'weapon')),
      shield: str(pick(raw, base + 8, 'Shield', 'shield'))
    }
  };
}

export interface PartyView {
  /** Guid vazio = sem grupo, e o gump some. */
  readonly id: string;
  readonly leaderId: string;
  readonly members: readonly PartyMemberView[];
  readonly canInvite: boolean;
  readonly canKick: boolean;
}

export interface PartyVitalView extends Pick<PartyMemberView, 'mana' | 'manaMax' | 'stam' | 'stamMax' | 'retrato'> {
  readonly characterId: string;
  readonly state: number;
  readonly hits: number;
  readonly hitsMax: number;
  readonly city: string;
}

function decodePartyMember(raw: unknown): PartyMemberView {
  return {
    characterId: guid(pick(raw, 0, 'CharacterId', 'characterId')),
    name: str(pick(raw, 1, 'Name', 'name')),
    leader: Boolean(pick(raw, 2, 'Leader', 'leader')),
    state: num(pick(raw, 3, 'State', 'state')),
    hits: num(pick(raw, 4, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 5, 'HitsMax', 'hitsMax')),
    city: str(pick(raw, 6, 'City', 'city')),
    unknown: Boolean(pick(raw, 7, 'Unknown', 'unknown')),
    ...decodeExtrasDoMembro(raw, 8)
  };
}

function decodePartyState(raw: unknown): PartyView {
  return {
    id: guid(pick(raw, 0, 'Id', 'id')),
    leaderId: guid(pick(raw, 1, 'LeaderId', 'leaderId')),
    members: arr(pick(raw, 2, 'Members', 'members')).map(decodePartyMember),
    canInvite: Boolean(pick(raw, 3, 'CanInvite', 'canInvite')),
    canKick: Boolean(pick(raw, 4, 'CanKick', 'canKick'))
  };
}

function decodePartyVital(raw: unknown): PartyVitalView {
  return {
    characterId: guid(pick(raw, 0, 'CharacterId', 'characterId')),
    state: num(pick(raw, 1, 'State', 'state')),
    hits: num(pick(raw, 2, 'Hits', 'hits')),
    hitsMax: num(pick(raw, 3, 'HitsMax', 'hitsMax')),
    city: str(pick(raw, 4, 'City', 'city')),
    ...decodeExtrasDoMembro(raw, 5)
  };
}

function decodeCatchup(raw: unknown): ChatCatchup {
  return {
    global: arr(pick(raw, 0, 'Global', 'global')).map(decodeChatLine).filter((x): x is ChatLine => x !== null),
    trade: arr(pick(raw, 1, 'Trade', 'trade')).map(decodeChatLine).filter((x): x is ChatLine => x !== null),
    guild: arr(pick(raw, 2, 'Guild', 'guild')).map(decodeChatLine).filter((x): x is ChatLine => x !== null),
    alliance: arr(pick(raw, 3, 'Alliance', 'alliance')).map(decodeChatLine).filter((x): x is ChatLine => x !== null)
  };
}

export interface GuildInvite {
  readonly name: string;
  readonly tag: string;
  readonly guildName: string;
}

export interface AllianceInvite {
  readonly fromTag: string;
  readonly fromName: string;
}

export interface WarInvite {
  readonly fromTag: string;
  readonly fromName: string;
  readonly format: string;
}

function decodeGuildInvite(raw: unknown): GuildInvite {
  return {
    name: str(pick(raw, 0, 'Name', 'name')),
    tag: str(pick(raw, 1, 'Tag', 'tag')),
    guildName: str(pick(raw, 2, 'GuildName', 'guildName'))
  };
}

function decodeAllianceInvite(raw: unknown): AllianceInvite {
  return {
    fromTag: str(pick(raw, 0, 'FromTag', 'fromTag')),
    fromName: str(pick(raw, 1, 'FromName', 'fromName'))
  };
}

function decodeDueloRodada(raw: unknown): DueloRodada {
  const quem = str(pick(raw, 0, 'Quem', 'quem'));
  return {
    quem: quem === 'voce' || quem === 'ele' ? quem : 'empate',
    por: str(pick(raw, 1, 'Por', 'por')) || 'morte',
    vidaVoce: num(pick(raw, 2, 'VidaVoce', 'vidaVoce')),
    vidaEle: num(pick(raw, 3, 'VidaEle', 'vidaEle'))
  };
}

function decodeWarInvite(raw: unknown): WarInvite {
  return {
    fromTag: str(pick(raw, 0, 'FromTag', 'fromTag')),
    fromName: str(pick(raw, 1, 'FromName', 'fromName')),
    format: str(pick(raw, 2, 'Format', 'format'))
  };
}

export function connectCityHub(
  world: WorldHandle,
  characterId: string,
  onChat: (line: ChatLine) => void,
  onCatchup: (pack: ChatCatchup) => void,
  extras: {
    onTell?: (line: ChatLine) => void;
    onTellCatchup?: (pack: TellCatchup) => void;
    onTellNotice?: (message: string) => void;
    /** Acabou de mandar um tell: a recusa genérica do chat (2, 7) que chegar agora é dele e vai para a conversa. */
    tellPendente?: () => boolean;
    onTradeInvite?: (name: string) => void;
    onTradeState?: (state: TradeState) => void;
    onTradeOver?: (over: TradeOver) => void;
    onDueloConvite?: (convite: DueloConvite) => void;
    /** O seu desafio saiu e espera resposta (duelo v2). */
    onDueloEnviado?: (enviado: DueloEnviado) => void;
    /** O convite acabou (aceito, recusado, cancelado ou vencido): some o cartão e a pílula. */
    onDueloConviteFim?: () => void;
    onDueloEstado?: (estado: DueloEstado) => void;
    onDueloVida?: (vida: DueloVida) => void;
    onDueloVoltar?: (volta: DueloVoltar) => void;
    onCastRefuse?: () => void;
    /** "cs": a sua conjuração começou (ms > 0, com a duração do servidor) ou parou (ms = 0). */
    onCastEstado?: (spell: string, ms: number, trava: boolean) => void;
    /** "cd": a recarga de uma magia (techspec-combate-v2). Magia vazia com 0 e 0 apaga todas (rodada do duelo). */
    onRecargaMagia?: (spell: string, ms: number, globalMs: number) => void;
    /** "va": a viagem foi aceita (portal ou menu Viajar). */
    onViagemAceita?: () => void;
    /** "cm": uma casa do mapa mudou (techspec-casas §03.16). */
    onCasasMudaram?: (mapaId: string) => void;
    /** "ru": o livro de runas da conta mudou (techspec-livro-de-runas); relê api/runas. */
    onRunasMudaram?: () => void;
    /** "rk": o Mark (tipo 0) ou o portal (tipo 1) começou (ativo, com ms) ou acabou (ok diz se deu certo). */
    onRunaRitual?: (r: RunaRitual) => void;
    /** "rf": o Recall da runa saiu, mas a viagem foi recusada (espera, mapa…); o aviso vem junto. */
    onRunaRecallFalhou?: () => void;
    /** "pg": um portal de runa no chão desta sala; "px": ele fechou. */
    onPortalRuna?: (p: PortalRunaFio) => void;
    onPortalRunaFim?: (id: string) => void;
    /** Poção/bandagem recusada de verdade (vazia, cheia, sem veneno). Espera compartilhada não entra. */
    onConsumeRefuse?: () => void;
    /** "b": a conta entrou em outro aparelho, aba ou personagem; esta sessão acabou. */
    onSessaoSubstituida?: () => void;
    /** Aviso 119: a IA da caçada usou um consumível; a barra mostra a recarga do slot igual. */
    onConsumeUsed?: (id: string, esperaMs: number) => void;
    /** Aviso 39/40: o modo da caçada trocou. Não há como entrar em Manual sem ele. */
    onHuntMode?: (manual: boolean) => void;
    /** "HuntFim": a ordem de caçada foi apagada no servidor (cancelou, acabou, GM). O mundo relê /hunts. */
    onHuntFim?: () => void;
    /** Aviso 147: a conta perdeu o acesso à fase de testes; o servidor fecha a conexão em seguida. */
    onAcessoEncerrado?: () => void;
    onGuildInvite?: (invite: GuildInvite) => void;
    onAllianceInvite?: (invite: AllianceInvite) => void;
    onWarInvite?: (invite: WarInvite) => void;
    onCorpseContents?: (pack: CorpseContents) => void;
    onCorpseGone?: (id: number) => void;
    onPackDirty?: () => void;
    onBankOpen?: (npc: { npcId: string; nome: string; x: number; y: number; shortId: number; alcance: number }) => void;
    onResEstado?: (estado: { etapa: number; pontoId: string; cidade: string; x: number; y: number }) => void;
    /** Criminoso ou assassino não revive em cidade com guarda: o estado e a oferta de Cove (notoriedade 0 = liberado). */
    onResBloqueio?: (aviso: { notoriedade: number; segundos: number; mortes: number; cidade: string; cidadeLivre: string }) => void;
    /** A oferta de Ressurreição (techspec-ressurreicao): quem oferece e os segundos; segundos 0 fecha a janela. */
    onResOferta?: (oferta: { de: string; segundos: number }) => void;
    /** O corpo da última morte ainda no chão (techspec-resgate-do-corpo); segundos 0 = nada para buscar. */
    onCorpoPendente?: (corpo: { mapa: string; segundos: number; itens: number; lista: { defId: string; qty: number; vestido: boolean }[] }) => void;
    /** O andamento do resgate do corpo: 0 nada, 1 indo, 2 recuperou, 3 não deu (motivo). */
    onResgate?: (estado: { etapa: number; motivo: string; cacadas: string[] }) => void;
    /** Convite de grupo. Letra própria: a carga tem a mesma forma da troca. */
    onPartyInvite?: (name: string) => void;
    onPartyState?: (state: PartyView) => void;
    onPartyVital?: (vital: PartyVitalView) => void;
    onCarryDelta?: (defId: string, qty: number) => void;
    onDummyTrain?: (skill: string, dummyId: number, skills: readonly { id: string; value: number }[]) => void;
    onTreinoAlvo?: (alvo: TreinoAlvo) => void;
    onEventoEstado?: (estado: import('../api/evento').EventoEstado) => void;
    /** Chefe do Mundo (techspec-chefe-mundo §07): a barra global. `id` vazio: não há chefe. */
    onChefeEstado?: (estado: import('../api/evento').ChefeEstado) => void;
    /** Pentagrama (techspec-pentagrama §09): a edição ao vivo (`cor` −1 = nenhuma), o canal e o altar da sala. */
    onPentaEstado?: (estado: import('../api/evento').PentaEstado) => void;
    onPentaCanal?: (canal: import('../api/evento').PentaCanal | null, ok: boolean) => void;
    onPentaAltar?: (linhas: readonly import('../api/evento').PentaAltarLinha[]) => void;
    /** Staff: entrou na sala oculto ou visível (o welcome diz). O cliente acerta o cinza e o selo do topo. */
    onOculto?: (ligado: boolean) => void;
    onVip?: (estado: { ate: number; mostrar: boolean; prata: number; semBanco: boolean }) => void;
    /** Coleta (techspec-coleta §07): o estado do gump. */
    onColeta?: (estado: ColetaAoVivo) => void;
    /** Aviso 164: o passo/ação foi recusado porque está coletando. */
    onColetaTravado?: () => void;
    /**
     * Avisos 99 (skill subiu: id e quanto), 167 (atributo subiu: str/dex/int e o valor novo) e 255 (barras de XP): o perfil
     * aberto se acerta na hora, sem reler a ficha (o ganho vem em rajada e a leitura tem limite por minuto).
     */
    onGanhoDoPerfil?: (ganho: GanhoDoPerfil) => void;
    onVipDebito?: (debito: { def: string; preco: number; prata: number }) => void;
    /** techspec-pocoes: os efeitos de poção ativos (fim em ms Unix do servidor). */
    onBuffs?: (buffs: readonly BuffAtivo[]) => void;
    /** "bo": os efeitos de outro jogador ou de um monstro, para a placa do alvo (techspec-magias-de-efeito). */
    onEfeitosDeOutro?: (id: number, buffs: readonly BuffAtivo[]) => void;
    onAvisoFaixa?: (aviso: {
      code: number;
      args: readonly string[];
      realce: RealceFaixa;
      texto: string;
    }) => void;
  } = {}
): {
  connection: HubConnection;
  sendStep: (seq: number, dir: number, gallop: boolean) => void;
  sendCast: (slot: number, targetId: number) => void;
  sendAttack: (targetId: number) => void;
  sendTrainDummy: (dummyId: number, skill: string) => void;
  sendStopTrainDummy: () => void;
  sendTreinarAuto: (skill: string) => Promise<void>;
  sendPortal: (portalId: string) => Promise<boolean>;
  sendPortalMenu: (portalId: string) => Promise<boolean>;
  /** `protegida`: a chave "Área protegida" do menu Teleportar (techspec-cacada-protegida §02.1). */
  sendViagem: (destinoId: string, protegida?: boolean) => Promise<boolean>;
  sendRes: () => Promise<void>;
  sendResCove: () => Promise<void>;
} {
  const connection = new HubConnectionBuilder()
    .withUrl('/hub/city', {
      withCredentials: true,
      headers: { 'X-UoIdle-Shard': shardHeaderValue() }
    })
    .withHubProtocol(new MessagePackHubProtocol())
    .configureLogging(LogLevel.Warning)
    .withAutomaticReconnect()
    .build();
  /** Outra sessão da mesma conta entrou ("b"): esta não reconecta sozinha. */
  let substituida = false;

  connection.on('w', (raw: unknown) => {
    const w = decodeWelcome(raw);
    world.applyWelcome(w);
    extras.onOculto?.(!!w.oculto);
    world.applyMundoLabel(w);
    void receberMundo(w.mundoEtag ?? '', w.esquemaVersao ?? 0, {
      avisar: () => world.sayKey('mundo.versaoDesatualizada', undefined, 'warn'),
      recarregarNpcs: etag => world.recarregarNpcs(etag)
    });
  });
  connection.on('q', (raw: unknown) => {
    const p = decodePong(raw);
    world.applyMundoLabel(p);
    void receberMundo(p.mundoEtag, p.esquemaVersao, {
      avisar: () => world.sayKey('mundo.versaoDesatualizada', undefined, 'warn'),
      recarregarNpcs: etag => world.recarregarNpcs(etag)
    });
  });
  connection.on('M', (raw: unknown) => {
    const etag = typeof raw === 'string' ? raw : str(raw);
    void receberMundo(etag, 0, {
      avisar: () => world.sayKey('mundo.versaoDesatualizada', undefined, 'warn'),
      recarregarNpcs: e => world.recarregarNpcs(e)
    });
  });
  connection.on('n', (raw: unknown) => {
    const entities = arr(pick(raw, 1, 'Entities', 'entities')).map(decodeEntered);
    world.applySnapshot(entities);
  });
  connection.on('f', (raw: unknown) => world.applyFrame(decodeFrame(raw)));
  connection.on('r', (raw: unknown) => world.applyReject(decodeReject(raw)));
  connection.on('e', (raw: unknown) => {
    const code = num(pick(raw, 0, 'Code', 'code'));
    const args = decodeNoticeArgs(raw);
    const legado = decodeNoticeLegado(raw);
    const realce = decodeNoticeRealce(raw);
    const def = defDoAviso(code, args);
    const tom = def?.tom ?? (code === 35 ? 'note' : 'warn');
    if ((TELL_NOTICE.has(code) || (TELL_RECUSA.has(code) && extras.tellPendente?.())) && extras.onTellNotice) {
      extras.onTellNotice(textoDoAviso(code, args, legado));
    }
    if (code === 39 || code === 40) extras.onHuntMode?.(code === 39);
    if (code === 147) extras.onAcessoEncerrado?.();
    if (code === 148) avisarSuporte();
    // 204: carta nova. 160 e os avisos de prêmio de evento também põem carta no correio (techspec-correio §07).
    if (code === 204) avisarCorreio();
    else if (CORREIO_RELE.has(code)) avisarCorreio(false);
    if (code === 41) extras.onHuntMode?.(false);
    if (CAST_REFUSE.has(code)) {
      extras.onCastRefuse?.();
      world.applyFizzle();
    }
    if (CONSUME_ABORT.has(code)) extras.onConsumeRefuse?.();
    if (code === 119) {
      if (args[0]) playConsumivel(args[0]);
      extras.onConsumeUsed?.(args[0] ?? '', Number(args[1] ?? 0));
      return;
    }
    // Coleta: o golpe sobe sobre o personagem a cada 4 s — no diário seria barulho.
    if (code === 162) {
      flutuarSobreMim(`+${args[1] ?? ''} ${nomeDoItem(args[0] ?? 'log')}`);
      return;
    }
    if (code === 163) {
      flutuarSobreMim(t(args[0] === 'minerio' ? 'coleta.minerio.res.erro' : 'coleta.res.erro'));
      return;
    }
    if (code === 164) extras.onColetaTravado?.();
    // O aviso de ataque do UO (241 você ataca, 242 atacam você): vermelho sobre a sua cabeça, e também no diário.
    if (code === 241 || code === 242) avisoSobreMim(textoDoAviso(code, args, legado));
    // A barra de XP das skills: só o perfil usa, nunca o diário.
    if (code === 255) {
      const barras = barrasDoAviso(args);
      if (barras.length) extras.onGanhoDoPerfil?.({ tipo: 'xp', id: '', valor: 0, barras });
      return;
    }
    if (code === 99 && args[0]) {
      extras.onGanhoDoPerfil?.({ tipo: 'skill', id: args[0], valor: Number(args[1] ?? 0) });
      somarSkillMagia(args[0], Number(args[1] ?? 0));
    }
    if (code === 167 && args[0]) extras.onGanhoDoPerfil?.({ tipo: 'atributo', id: args[0], valor: Number(args[1] ?? 0) });
    if (COMBATE_NO_FRAME.has(code)) return;
    if (def) playEvento(def.chave);
    if (code === 0 && legado) {
      world.say(legado, 'system');
    } else if (def) {
      world.sayKey(def.chave, argsDoAviso(def, argsDoEvento(code, args)), tom);
    } else {
      world.say(legado || t('mundo.avisoDoServidor'), tom);
    }
    if (realce) {
      extras.onAvisoFaixa?.({
        code,
        args,
        realce,
        texto: textoDoAviso(code, args, legado)
      });
    }
  });
  // "b": a conta entrou em outro lugar (outro aparelho, aba ou personagem). Uma sessão por conta (Renato, 02/11/2026):
  // esta para de vez. Sem o stop, a reconexão automática pedia a entrada de novo e tomava a vaga de volta.
  connection.on('b', (raw: unknown) => {
    substituida = true;
    const reason = typeof raw === 'string' ? raw : decodeNoticeLegado(raw);
    if (reason) world.say(reason, 'warn');
    else world.sayKey('mundo.sessaoEncerrada', undefined, 'warn');
    extras.onSessaoSubstituida?.();
    void connection.stop();
  });
  connection.on('h', (raw: unknown) => {
    const line = decodeChatLine(raw);
    if (!line) return;
    if (line.via === 3) extras.onTell?.(line);
    else onChat(line);
  });
  connection.on('k', (raw: unknown) => onCatchup(decodeCatchup(raw)));
  connection.on('j', (raw: unknown) => extras.onTellCatchup?.(decodeTellCatchup(raw)));
  connection.on('i', (raw: unknown) => extras.onTradeInvite?.(decodeTradeInvite(raw).name));
  connection.on('d', (raw: unknown) => extras.onPartyInvite?.(str(pick(raw, 0, 'Name', 'name'))));
  connection.on('c', (raw: unknown) => extras.onPartyState?.(decodePartyState(raw)));
  connection.on('m', (raw: unknown) => extras.onPartyVital?.(decodePartyVital(raw)));
  connection.on('HuntFim', () => extras.onHuntFim?.());
  connection.on('x', (raw: unknown) => extras.onTradeState?.(decodeTradeState(raw)));
  connection.on('z', (raw: unknown) => extras.onTradeOver?.(decodeTradeOver(raw)));
  connection.on('co', (raw: unknown) => extras.onColeta?.(decodeColeta(raw)));
  connection.on('ve', (raw: unknown) => extras.onVip?.({
    ate: num(pick(raw, 0, 'Ate', 'ate')),
    mostrar: flag(pick(raw, 1, 'Mostrar', 'mostrar')),
    prata: num(pick(raw, 2, 'Prata', 'prata')),
    semBanco: flag(pick(raw, 3, 'SemBanco', 'semBanco'))
  }));
  // techspec-pocoes: os efeitos de poção ativos, inteiros a cada mudança.
  connection.on('bf', (raw: unknown) => extras.onBuffs?.(arr(pick(raw, 0, 'Itens', 'itens')).map(b => ({
    tipo: str(pick(b, 0, 'Tipo', 'tipo')),
    item: str(pick(b, 1, 'Item', 'item')),
    valor: num(pick(b, 2, 'Valor', 'valor')),
    fim: num(pick(b, 3, 'Fim', 'fim')),
    total: num(pick(b, 4, 'Total', 'total'))
  })).filter(b => b.tipo)));
  connection.on('bo', (raw: unknown) => extras.onEfeitosDeOutro?.(
    num(pick(raw, 0, 'Id', 'id')),
    arr(pick(raw, 1, 'Itens', 'itens')).map(b => ({
      tipo: str(pick(b, 0, 'Tipo', 'tipo')),
      item: str(pick(b, 1, 'Item', 'item')),
      valor: num(pick(b, 2, 'Valor', 'valor')),
      fim: num(pick(b, 3, 'Fim', 'fim')),
      total: num(pick(b, 4, 'Total', 'total'))
    })).filter(b => b.tipo)));
  connection.on('vp', (raw: unknown) => extras.onVipDebito?.({
    def: str(pick(raw, 0, 'Def', 'def')),
    preco: num(pick(raw, 1, 'Preco', 'preco')),
    prata: num(pick(raw, 2, 'Prata', 'prata'))
  }));
  connection.on('l', (raw: unknown) => {
    const id = num(pick(raw, 0, 'Id', 'id'));
    const mount = str(pick(raw, 1, 'Mount', 'mount'));
    const skin = str(pick(raw, 2, 'Skin', 'skin'));
    // Look completo: Dead omitido (false no MessagePack) = vivo.
    const rawNoto = pick(raw, 5, 'Notoriety', 'notoriety');
    const rawTag = pick(raw, 6, 'GuildTag', 'guildTag');
    const rawRel = pick(raw, 7, 'Rel', 'rel');
    world.setLook(id, {
      mount,
      skin: skin || undefined,
      dead: flag(pick(raw, 3, 'Dead', 'dead')),
      cloth: str(pick(raw, 4, 'Cloth', 'cloth')),
      weapon: optionalStr(pick(raw, 8, 'Weapon', 'weapon')),
      shield: optionalStr(pick(raw, 9, 'Shield', 'shield')),
      hairStyle: optionalStr(pick(raw, 10, 'HairStyle', 'hairStyle')),
      hairHue: num(pick(raw, 11, 'HairHue', 'hairHue')) || undefined,
      beardStyle: optionalStr(pick(raw, 12, 'BeardStyle', 'beardStyle')),
      beardHue: num(pick(raw, 13, 'BeardHue', 'beardHue')) || undefined,
      notoriety: rawNoto === undefined || rawNoto === null ? undefined : num(rawNoto),
      guildTag: rawTag === undefined || rawTag === null ? undefined : str(rawTag),
      rel: rawRel === undefined || rawRel === null ? undefined : num(rawRel),
      vip: pick(raw, 14, 'Vip', 'vip') === undefined ? undefined : flag(pick(raw, 14, 'Vip', 'vip')),
      selo: pick(raw, 15, 'Selo', 'selo') === undefined ? undefined : num(pick(raw, 15, 'Selo', 'selo'))
    });
  });
  connection.on('g', (raw: unknown) => extras.onGuildInvite?.(decodeGuildInvite(raw)));
  connection.on('o', (raw: unknown) => extras.onAllianceInvite?.(decodeAllianceInvite(raw)));
  connection.on('u', (raw: unknown) => extras.onWarInvite?.(decodeWarInvite(raw)));
  connection.on('v', (raw: unknown) => {
    world.setVitals(
      num(pick(raw, 0, 'Id', 'id')),
      num(pick(raw, 1, 'Hits', 'hits')),
      num(pick(raw, 2, 'HitsMax', 'hitsMax')),
      num(pick(raw, 3, 'Mana', 'mana')),
      num(pick(raw, 4, 'ManaMax', 'manaMax')),
      flag(pick(raw, 5, 'Poisoned', 'poisoned'))
    );
  });
  connection.on('cs', (raw: unknown) => {
    extras.onCastEstado?.(
      str(pick(raw, 0, 'Spell', 'spell')),
      num(pick(raw, 1, 'Ms', 'ms')),
      flag(pick(raw, 2, 'Trava', 'trava'))
    );
  });
  // "va": a sala aceitou a viagem (portal ou menu Viajar); o som sai aqui, não no clique.
  connection.on('va', () => { extras.onViagemAceita?.(); });
  // "nv": a versão da interface publicada (ao conectar e no deploy só da interface; techspec-atualizacao-do-cliente).
  connection.on('nv', (raw: unknown) => { receberVersaoWeb(str(pick(raw, 0, 'Versao', 'versao'))); });
  // "cm": uma casa deste mapa mudou (comprou, devolveu, acesso, baú, item preso): relê as casas na hora.
  connection.on('cm', (raw: unknown) => { extras.onCasasMudaram?.(str(pick(raw, 0, 'Mapa', 'mapa'))); });
  // Livro de runas (techspec-livro-de-runas): o livro mudou, a conjuração do Mark/portal, os portais no chão.
  connection.on('ru', () => { extras.onRunasMudaram?.(); });
  connection.on('rk', (raw: unknown) => {
    extras.onRunaRitual?.({
      tipo: (['mark', 'portal', 'recall'] as const)[num(pick(raw, 0, 'Tipo', 'tipo'))] ?? 'mark',
      ms: num(pick(raw, 1, 'Ms', 'ms')),
      ativo: flag(pick(raw, 2, 'Ativo', 'ativo')),
      ok: flag(pick(raw, 3, 'Ok', 'ok')),
      alvo: str(pick(raw, 4, 'Alvo', 'alvo'))
    });
  });
  connection.on('pg', (raw: unknown) => {
    const id = str(pick(raw, 0, 'Id', 'id'));
    if (!/^rg-[a-f0-9]{1,32}$/.test(id)) return;
    extras.onPortalRuna?.({
      id,
      x: num(pick(raw, 1, 'X', 'x')),
      y: num(pick(raw, 2, 'Y', 'y')),
      vermelho: flag(pick(raw, 3, 'Vermelho', 'vermelho')),
      rotulo: str(pick(raw, 4, 'Rotulo', 'rotulo')).slice(0, 64),
      ms: num(pick(raw, 5, 'Ms', 'ms')),
      dono: str(pick(raw, 6, 'Dono', 'dono')),
      destinoMapa: str(pick(raw, 7, 'DestinoMapa', 'destinoMapa')),
      protegida: flag(pick(raw, 8, 'Protegida', 'protegida'))
    });
  });
  connection.on('px', (raw: unknown) => { extras.onPortalRunaFim?.(str(pick(raw, 0, 'Id', 'id'))); });
  connection.on('rf', () => { extras.onRunaRecallFalhou?.(); });
  connection.on('cd', (raw: unknown) => {
    extras.onRecargaMagia?.(
      str(pick(raw, 0, 'Spell', 'spell')),
      num(pick(raw, 1, 'Ms', 'ms')),
      num(pick(raw, 2, 'GlobalMs', 'globalMs'))
    );
  });
  connection.on('y', (raw: unknown) => {
    world.applySpeech(
      num(pick(raw, 0, 'Id', 'id')),
      str(pick(raw, 1, 'Text', 'text')),
      num(pick(raw, 2, 'Kind', 'kind'))
    );
  });
  connection.on('a', (raw: unknown) => {
    const spell = str(pick(raw, 1, 'Spell', 'spell'));
    const casterId = num(pick(raw, 0, 'Id', 'id'));
    const fromX = num(pick(raw, 2, 'FromX', 'fromX'));
    const fromY = num(pick(raw, 3, 'FromY', 'fromY'));
    const toX = num(pick(raw, 4, 'ToX', 'toX'));
    const toY = num(pick(raw, 5, 'ToY', 'toY'));
    const inicio = num(pick(raw, 6, 'StartMs', 'startMs'));
    const duracaoMs = num(pick(raw, 7, 'DurationMs', 'durationMs'));
    const alvoId = num(pick(raw, 8, 'Alvo', 'alvo'));
    const folha = folhaDaMagia(spell);
    if (duracaoMs > 0) {
      // O projétil persegue o alvo (Flecha Mágica, Bola de Fogo, Raio de Energia): techspec-magias-animacoes.
      world.spawnEffect(folha, { x: fromX, y: fromY }, { x: toX, y: toY }, inicio, duracaoMs, casterId, 0, alvoId);
      return;
    }
    world.playSpell(spell, casterId);
    // O Recall deixa a fumaça no lugar de onde saiu: não acompanha quem viajou.
    if (ehEfeitoAlvo(folha))
      world.spawnEffect(folha, { x: fromX, y: fromY }, { x: toX, y: toY }, inicio, 0, casterId, spell === 'recall' ? 0 : alvoId);
  });
  connection.on('CorpseContents', (raw: unknown) => extras.onCorpseContents?.(decodeCorpseContents(raw)));
  connection.on('CorpseGone', (raw: unknown) => extras.onCorpseGone?.(num(pick(raw, 0, 'Id', 'id'))));
  connection.on('PackDirty', () => extras.onPackDirty?.());
  connection.on('BankOpen', (raw: unknown) => extras.onBankOpen?.({
    npcId: str(pick(raw, 0, 'NpcId', 'npcId')),
    nome: str(pick(raw, 1, 'Nome', 'nome')),
    x: num(pick(raw, 2, 'X', 'x')),
    y: num(pick(raw, 3, 'Y', 'y')),
    shortId: num(pick(raw, 4, 'ShortId', 'shortId')),
    alcance: num(pick(raw, 5, 'Alcance', 'alcance'), 2)
  }));
  connection.on('ResBloqueio', (raw: unknown) => extras.onResBloqueio?.({
    notoriedade: num(pick(raw, 0, 'Notoriedade', 'notoriedade')),
    segundos: num(pick(raw, 1, 'Segundos', 'segundos')),
    mortes: num(pick(raw, 2, 'Mortes', 'mortes')),
    cidade: str(pick(raw, 3, 'Cidade', 'cidade')),
    cidadeLivre: str(pick(raw, 4, 'CidadeLivre', 'cidadeLivre'))
  }));
  connection.on('ResOferta', (raw: unknown) => extras.onResOferta?.({
    de: str(pick(raw, 0, 'De', 'de')),
    segundos: num(pick(raw, 1, 'Segundos', 'segundos'))
  }));
  connection.on('cp', (raw: unknown) => {
    const lista = pick(raw, 3, 'Lista', 'lista');
    extras.onCorpoPendente?.({
      mapa: str(pick(raw, 0, 'Mapa', 'mapa')),
      segundos: num(pick(raw, 1, 'Segundos', 'segundos')),
      itens: num(pick(raw, 2, 'Itens', 'itens')),
      // Os itens da janela "Corpo" (resgate-corpo-prototipo); servidor antigo não manda: a grade fica vazia.
      lista: Array.isArray(lista)
        ? lista.slice(0, 60).map(it => ({
          defId: str(pick(it, 0, 'DefId', 'defId')),
          qty: Math.max(1, num(pick(it, 1, 'Qty', 'qty'))),
          vestido: !!pick(it, 2, 'Vestido', 'vestido')
        })).filter(it => it.defId)
        : []
    });
  });
  connection.on('rg', (raw: unknown) => {
    const lista = pick(raw, 2, 'Cacadas', 'cacadas');
    extras.onResgate?.({
      etapa: num(pick(raw, 0, 'Etapa', 'etapa')),
      motivo: str(pick(raw, 1, 'Motivo', 'motivo')),
      cacadas: Array.isArray(lista) ? lista.map(x => String(x)) : []
    });
  });
  connection.on('ResEstado', (raw: unknown) => extras.onResEstado?.({
    etapa: num(pick(raw, 0, 'Etapa', 'etapa')),
    pontoId: str(pick(raw, 1, 'PontoId', 'pontoId')),
    cidade: str(pick(raw, 2, 'Cidade', 'cidade')),
    x: num(pick(raw, 3, 'X', 'x')),
    y: num(pick(raw, 4, 'Y', 'y'))
  }));
  connection.on('t', (raw: unknown) => {
    const d = decodeCarryDelta(raw);
    if (d.defId) extras.onCarryDelta?.(d.defId, d.qty);
  });
  // Hora de Britannia (techspec-dia-e-noite §02): ao entrar e quando o GM muda.
  connection.on('Hora', (raw: unknown) => sincronizarHora(
    num(pick(raw, 0, 'Minuto', 'minuto'), Number.NaN),
    num(pick(raw, 1, 'MsPorMinuto', 'msPorMinuto'), MS_POR_MINUTO)
  ));
  connection.on('TreinoAlvo', (raw: unknown) => extras.onTreinoAlvo?.({
    dummyId: num(pick(raw, 0, 'DummyId', 'dummyId')),
    x: num(pick(raw, 1, 'X', 'x')),
    y: num(pick(raw, 2, 'Y', 'y')),
    alcance: num(pick(raw, 3, 'Alcance', 'alcance'), 1),
    skill: str(pick(raw, 4, 'Skill', 'skill')),
    recall: !!pick(raw, 5, 'Recall', 'recall')
  }));
  connection.on('DummyTrain', (raw: unknown) => {
    extras.onDummyTrain?.(
      str(pick(raw, 0, 'Skill', 'skill')),
      num(pick(raw, 1, 'DummyId', 'dummyId')),
      arr(pick(raw, 2, 'Skills', 'skills')).map(row => ({
        id: str(pick(row, 0, 'Id', 'id')),
        value: num(pick(row, 1, 'Value', 'value'))
      })).filter(s => s.id)
    );
  });
  connection.on('DueloConvite', (raw: unknown) => extras.onDueloConvite?.({
    de: str(pick(raw, 0, 'De', 'de')),
    modo: str(pick(raw, 1, 'Modo', 'modo')),
    expiraEmMs: num(pick(raw, 2, 'ExpiraEmMs', 'expiraEmMs')),
    comAposta: flag(pick(raw, 3, 'ComAposta', 'comAposta'))
  }));
  connection.on('DueloEnviado', (raw: unknown) => extras.onDueloEnviado?.({
    para: str(pick(raw, 0, 'Para', 'para')),
    modo: str(pick(raw, 1, 'Modo', 'modo')),
    comAposta: flag(pick(raw, 2, 'ComAposta', 'comAposta')),
    expiraEmMs: num(pick(raw, 3, 'ExpiraEmMs', 'expiraEmMs'))
  }));
  connection.on('DueloConviteFim', () => extras.onDueloConviteFim?.());
  connection.on('DueloEstado', (raw: unknown) => extras.onDueloEstado?.({
    duelo: guid(pick(raw, 0, 'Duelo', 'duelo')),
    modo: str(pick(raw, 1, 'Modo', 'modo')),
    estado: str(pick(raw, 2, 'Estado', 'estado')),
    rodada: num(pick(raw, 3, 'Rodada', 'rodada')),
    placarVoce: num(pick(raw, 4, 'PlacarVoce', 'placarVoce')),
    placarEle: num(pick(raw, 5, 'PlacarEle', 'placarEle')),
    rodadasPara: num(pick(raw, 6, 'RodadasPara', 'rodadasPara')) || 2,
    lutaComecaEmMs: num(pick(raw, 7, 'LutaComecaEmMs', 'lutaComecaEmMs')),
    rodadaTerminaEmMs: num(pick(raw, 8, 'RodadaTerminaEmMs', 'rodadaTerminaEmMs')),
    saqueAteMs: num(pick(raw, 9, 'SaqueAteMs', 'saqueAteMs')),
    agoraServidorMs: num(pick(raw, 10, 'AgoraServidorMs', 'agoraServidorMs')),
    adversario: str(pick(raw, 11, 'Adversario', 'adversario')),
    contagemMs: num(pick(raw, 12, 'ContagemMs', 'contagemMs')),
    rodadaDuracaoMs: num(pick(raw, 13, 'RodadaDuracaoMs', 'rodadaDuracaoMs')),
    saqueTotalMs: num(pick(raw, 14, 'SaqueTotalMs', 'saqueTotalMs')),
    ausenteAteMs: num(pick(raw, 15, 'AusenteAteMs', 'ausenteAteMs')),
    vidaVoce: num(pick(raw, 16, 'VidaVoce', 'vidaVoce')),
    vidaEle: num(pick(raw, 17, 'VidaEle', 'vidaEle')),
    rodadas: arr(pick(raw, 18, 'Rodadas', 'rodadas')).map(decodeDueloRodada),
    desfecho: str(pick(raw, 19, 'Desfecho', 'desfecho')),
    vencedor: str(pick(raw, 20, 'Vencedor', 'vencedor')),
    voce: str(pick(raw, 21, 'Voce', 'voce')),
    carregarAteMs: num(pick(raw, 22, 'CarregarAteMs', 'carregarAteMs')),
    voceCarregou: flag(pick(raw, 23, 'VoceCarregou', 'voceCarregou')),
    eleCarregou: flag(pick(raw, 24, 'EleCarregou', 'eleCarregou')),
    comAposta: flag(pick(raw, 25, 'ComAposta', 'comAposta'))
  }));
  connection.on('DueloVida', (raw: unknown) => extras.onDueloVida?.({
    voce: num(pick(raw, 0, 'Voce', 'voce')),
    ele: num(pick(raw, 1, 'Ele', 'ele'))
  }));
  connection.on('DueloVoltar', (raw: unknown) => extras.onDueloVoltar?.({
    adversario: str(pick(raw, 0, 'Adversario', 'adversario')),
    placar: str(pick(raw, 1, 'Placar', 'placar')),
    expiraEmMs: num(pick(raw, 2, 'ExpiraEmMs', 'expiraEmMs'))
  }));
  connection.on('EventoEstado', (raw: unknown) => {
    extras.onEventoEstado?.({
      estado: num(pick(raw, 0, 'Estado', 'estado')),
      inscrito: flag(pick(raw, 1, 'Inscrito', 'inscrito')),
      moeda: str(pick(raw, 2, 'Moeda', 'moeda')) || 'silver',
      taxa: num(pick(raw, 3, 'Taxa', 'taxa')),
      bolo: num(pick(raw, 4, 'Bolo', 'bolo')),
      inscritos: num(pick(raw, 5, 'Inscritos', 'inscritos')),
      fechaEmMs: num(pick(raw, 6, 'FechaEmMs', 'fechaEmMs')),
      fimEmMs: num(pick(raw, 7, 'FimEmMs', 'fimEmMs')),
      vivos: num(pick(raw, 8, 'Vivos', 'vivos')),
      portaAberta: flag(pick(raw, 9, 'PortaAberta', 'portaAberta')),
      modo: str(pick(raw, 10, 'Modo', 'modo')) || 'trajeDaCasa'
    });
  });

  connection.on('ChefeEstado', (raw: unknown) => {
    extras.onChefeEstado?.({
      id: str(pick(raw, 0, 'Id', 'id')),
      estado: num(pick(raw, 1, 'Estado', 'estado')),
      variante: str(pick(raw, 2, 'Variante', 'variante')),
      nome: str(pick(raw, 3, 'Nome', 'nome')),
      matiz: num(pick(raw, 4, 'Matiz', 'matiz')),
      mapa: str(pick(raw, 5, 'Mapa', 'mapa')),
      permil: num(pick(raw, 6, 'Permil', 'permil')),
      fimEmMs: num(pick(raw, 7, 'FimEmMs', 'fimEmMs')),
      lutadores: num(pick(raw, 8, 'Lutadores', 'lutadores'))
    });
  });

  connection.on('PentaEstado', (raw: unknown) => {
    extras.onPentaEstado?.({
      id: str(pick(raw, 0, 'Id', 'id')),
      cor: num(pick(raw, 1, 'Cor', 'cor'), -1),
      fase: num(pick(raw, 2, 'Fase', 'fase')),
      guardiaoEmMs: num(pick(raw, 3, 'GuardiaoEmMs', 'guardiaoEmMs')),
      fimEmMs: num(pick(raw, 4, 'FimEmMs', 'fimEmMs')),
      guardiaoMorto: flag(pick(raw, 5, 'GuardiaoMorto', 'guardiaoMorto')),
      lutadores: num(pick(raw, 6, 'Lutadores', 'lutadores'))
    });
  });
  connection.on('pc', (raw: unknown) => {
    const tipo = num(pick(raw, 0, 'Tipo', 'tipo')) === 2 ? 2 : 1;
    const ms = num(pick(raw, 2, 'Ms', 'ms'));
    const ativo = flag(pick(raw, 3, 'Ativo', 'ativo'));
    extras.onPentaCanal?.(ativo ? { tipo, cor: num(pick(raw, 1, 'Cor', 'cor'), -1), ms, ateMs: Date.now() + ms } : null,
      flag(pick(raw, 4, 'Ok', 'ok')));
  });
  connection.on('pa', (raw: unknown) => {
    const linhas = pick(raw, 0, 'Linhas', 'linhas');
    extras.onPentaAltar?.(Array.isArray(linhas)
      ? linhas.map(l => ({
          nome: str(pick(l, 0, 'Nome', 'nome')),
          mascara: num(pick(l, 1, 'Mascara', 'mascara')),
          ritual: flag(pick(l, 2, 'Ritual', 'ritual'))
        }))
      : []);
  });

  const sendStep = (seq: number, dir: number, gallop: boolean) => {
    if (connection.state !== HubConnectionState.Connected) return;
    void connection.send('s', seq, dir, gallop);
  };

  const sendCast = (slot: number, targetId: number) => {
    if (connection.state !== HubConnectionState.Connected) return;
    void connection.send('d', slot, targetId);
  };

  const sendAttack = (targetId: number) => {
    if (connection.state !== HubConnectionState.Connected) return;
    void connection.send('h', targetId);
  };

  const sendTrainDummy = (dummyId: number, skill: string) => {
    if (connection.state !== HubConnectionState.Connected) return;
    void connection.send('TrainDummy', dummyId & 0xffff, skill);
  };

  const sendStopTrainDummy = () => {
    if (connection.state !== HubConnectionState.Connected) return;
    void connection.send('StopTrainDummy');
  };

  // true = a viagem saiu; false = recusada (espera, caçando, em combate…): o jogo mostra a falha (Renato, 02/11/2026).
  const sendPortal = (portalId: string): Promise<boolean> => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve(false);
    return connection.invoke<boolean>('UsePortal', portalId);
  };

  const sendPortalMenu = (portalId: string): Promise<boolean> => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve(false);
    return connection.invoke<boolean>('UsePortalMenu', portalId);
  };

  const sendViagem = (destinoId: string, protegida = false): Promise<boolean> => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve(false);
    return protegida
      ? connection.invoke<boolean>('UseViagemArea', destinoId, true)
      : connection.invoke<boolean>('UseViagem', destinoId);
  };

  const sendTreinarAuto = (skill: string) => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve();
    return connection.invoke('TreinarAuto', skill);
  };

  const sendRes = () => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve();
    return connection.invoke('Res');
  };

  const sendResCove = () => {
    if (connection.state !== HubConnectionState.Connected) return Promise.resolve();
    return connection.invoke('ResCove');
  };

  connection.onreconnecting(() => anotarQueda());
  connection.onreconnected(() => {
    // A sessão foi tomada por outro aparelho: não volta sozinha (só pelo botão "Tentar de novo").
    if (substituida) return;
    void connection.invoke('EnterWorld', characterId);
  });

  return { connection, sendStep, sendCast, sendAttack, sendTrainDummy, sendStopTrainDummy, sendTreinarAuto, sendPortal, sendPortalMenu, sendViagem, sendRes, sendResCove };
}

export async function startCityHub(
  world: WorldHandle,
  connection: HubConnection,
  characterId: string,
  onFail: () => void
): Promise<void> {
  try {
    await connection.start();
    await connection.invoke('EnterWorld', characterId);
    let ultimoPing = 0;
    const ping = window.setInterval(() => {
      const agora = Date.now();
      const cada = overlayAberto() ? 2000 : 5000;
      if (agora - ultimoPing < cada) return;
      ultimoPing = agora;
      if (connection.state === HubConnectionState.Connected)
        void connection.send('p', agora);
    }, 1000);
    connection.onclose(() => window.clearInterval(ping));
  } catch (err) {
    const detail = err instanceof Error ? err.message : '';
    // O servidor já mandou o aviso (código 3). A HubException só poluía o diário.
    if (detail && !/HubException|Não autorizado|conta de operação/i.test(detail))
      world.sayKey('mundo.naoEntrouDetalhe', { detalhe: detail }, 'warn');
    else if (!detail)
      world.sayKey('mundo.enterWorldDenied', undefined, 'warn');
    onFail();
  }
}

export async function sayOnHub(
  connection: HubConnection,
  via: 0 | 1 | 2 | 4 | 5,
  text: string
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('t', via, text);
}

export async function tellOnHub(
  connection: HubConnection,
  toName: string,
  text: string
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) throw new Error('sem conexão');
  // invoke, não send: um erro no servidor volta para quem mandou em vez de sumir em silêncio.
  try {
    await connection.invoke('m', toName, text);
  } catch (err) {
    console.error('[tell] o servidor recusou o tell', err);
    throw err;
  }
}

export async function askTellCatchup(
  connection: HubConnection,
  withName: string
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('g', withName);
}

export async function inviteTradeOnHub(connection: HubConnection, name: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('u', name);
}

export async function respondTradeOnHub(connection: HubConnection, name: string, ok: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('v', name, ok);
}

export async function offerOnHub(
  connection: HubConnection, op: 1 | 2 | 3, itemId: string | null, qty: number
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('o', op, itemId, qty);
}

export async function readyTradeOnHub(connection: HubConnection, ready: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('a', ready);
}

export async function desafiarOnHub(connection: HubConnection, name: string, modo: string, aposta: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dD', name, modo, aposta);
}

/** Cancela o desafio que você mandou e ainda espera resposta (duelo v2). */
export async function cancelarDesafioOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dQ');
}

export async function responderDueloOnHub(connection: HubConnection, aceitar: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dR', aceitar);
}

/** Atalho de cancelar a magia: a conjuração em curso falha de propósito, sem gastar mana. */
/** Resgate do corpo (techspec-resgate-do-corpo): pergunta pelo corpo, pede o resgate ou desiste dele. */
/** O fim ou o começo do Mark ou do portal (evento "rk"). */
export interface RunaRitual {
  readonly tipo: 'mark' | 'portal' | 'recall';
  readonly ms: number;
  readonly ativo: boolean;
  readonly ok: boolean;
  /** A runa (ou "casa") do portal ou do remarcar; vazio no Mark novo. */
  readonly alvo: string;
}

/** Um portal de runa no chão (evento "pg"). */
export interface PortalRunaFio {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly vermelho: boolean;
  readonly rotulo: string;
  readonly ms: number;
  readonly dono: string;
  readonly destinoMapa: string;
  readonly protegida: boolean;
}

/** Marca (runaId nulo) ou remarca uma runa: o servidor conjura o Mark e responde pelos eventos "rk", "ru" e "e". */
export async function runaMarcarOnHub(connection: HubConnection, nome: string | null, cor: number, runaId: string | null): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('RunaMarcar', nome, cor, runaId);
}

/** Abre o portal de uma runa (ou da casa: "casa"). */
export async function runaPortalOnHub(connection: HubConnection, alvo: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('RunaPortal', alvo);
}

/** Recall de uma runa (ou da casa): o servidor conjura o Recall (o tempo da caçada) e depois viaja ("rk", "va", "rf"). */
export async function runaRecallOnHub(connection: HubConnection, runaId: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('RunaRecall', runaId);
}

export async function corpoPendenteOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('CorpoPendente');
}
export async function resgatarCorpoOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('ResgatarCorpo');
}
export async function cancelarResgateOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('CancelarResgate');
}

/** O fantasma responde à oferta de Ressurreição (techspec-ressurreicao). */
export async function responderRessOnHub(connection: HubConnection, aceita: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('rr', aceita);
}

export async function cancelarMagiaOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dc');
}

export async function desistirDueloOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dX');
}

export async function sairDueloOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dS');
}

/** A tela de carregamento terminou: na arena de duelo, o servidor só começa a contagem com os dois prontos. */
export async function dueloCarregouOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dP');
}

export async function voltarDueloOnHub(connection: HubConnection, voltar: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('dV', voltar);
}

export async function cancelTradeOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('c');
}

export async function openCorpseOnHub(connection: HubConnection, id: number): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.invoke('k', id & 0xffff);
}

export async function takeCorpseOnHub(
  connection: HubConnection, id: number, itemId: string, qty = 0
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.invoke('q', id & 0xffff, itemId, qty);
}

export async function takeCorpseAllOnHub(
  connection: HubConnection, id: number, slotIds: readonly string[]
): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.invoke('TakeCorpseAll', id & 0xffff, slotIds);
}

// ---- grupo (party) ----------------------------------------------------------

export async function invitePartyOnHub(connection: HubConnection, name: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('j', name);
}

/** O nome não vai: o convite é indexado por quem recebe. */
export async function respondPartyOnHub(connection: HubConnection, ok: boolean): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('l', ok);
}

export async function leavePartyOnHub(connection: HubConnection): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('n');
}

export async function kickPartyOnHub(connection: HubConnection, name: string): Promise<void> {
  if (connection.state !== HubConnectionState.Connected) return;
  await connection.send('r', name);
}
